import React, { useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

const makeRowId = () => `row-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
const normalizeRows = (rows) => (Array.isArray(rows) ? rows : []);

const QuestionFormWordGrid = ({ value, onChange }) => {
    const v = value || {};
    const rows = normalizeRows(v.wordRows);
    const [helpOpen, setHelpOpen] = useState(false);

    const updateRow = (idx, patch) => {
        onChange({
            ...v,
            wordRows: rows.map((row, rowIdx) => (rowIdx === idx ? { ...row, ...patch } : row)),
        });
    };

    const addRow = () => {
        onChange({
            ...v,
            wordRows: [...rows, { id: makeRowId(), clue: "", answer: "", offset: 0 }],
        });
    };

    const removeRow = (idx) => {
        onChange({
            ...v,
            wordRows: rows.filter((_, rowIdx) => rowIdx !== idx),
        });
    };

    return (
        <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
                <Typography variant="subtitle2">Сітка слів</Typography>
                <Tooltip title="Показати інструкцію для налаштування цього завдання">
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<HelpOutlineIcon />}
                        onClick={() => setHelpOpen(true)}
                    >
                        Як користуватися
                    </Button>
                </Tooltip>
            </Stack>

            <TextField
                label="Підзаголовок"
                value={v.subtitle || ""}
                onChange={(e) => onChange({ ...v, subtitle: e.target.value })}
                fullWidth
            />

            <TextField
                label="Текст завдання"
                value={v.question || ""}
                onChange={(e) => onChange({ ...v, question: e.target.value })}
                helperText="Наприклад: Розгадайте слова за підказками."
                fullWidth
                multiline
                minRows={3}
            />

            <TextField
                label="Інструкція (необов'язково)"
                value={v.instruction || ""}
                onChange={(e) => onChange({ ...v, instruction: e.target.value })}
                helperText="Коротка підказка для учня. Якщо порожньо, блок не показується."
                fullWidth
                multiline
                minRows={2}
            />

            <TextField
                label="Колір відкритих літер"
                type="color"
                value={v.revealedLetterColor || "#c62828"}
                onChange={(e) => onChange({ ...v, revealedLetterColor: e.target.value })}
                helperText="Літери у квадратних дужках будуть показані учню одразу, наприклад: та[р]ас."
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Box
                                sx={{
                                    width: 18,
                                    height: 18,
                                    borderRadius: "50%",
                                    backgroundColor: v.revealedLetterColor || "#c62828",
                                    border: "1px solid",
                                    borderColor: "divider",
                                }}
                            />
                        </InputAdornment>
                    ),
                }}
                sx={{ maxWidth: 360 }}
            />

            <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                        Рядки: {rows.length}
                    </Typography>
                    <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addRow}>
                        Додати рядок
                    </Button>
                </Stack>

                <Stack spacing={1.5}>
                    {rows.map((row, idx) => (
                        <Stack key={row.id || idx} direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "flex-start" }}>
                            <TextField
                                label={`Відповідь #${idx + 1}`}
                                value={row.answer || ""}
                                onChange={(e) => updateRow(idx, { answer: e.target.value })}
                                helperText="Щоб відкрити літеру одразу, візьміть її в [ ]."
                                sx={{ minWidth: { md: 220 } }}
                            />
                            <TextField
                                label="Зсув"
                                type="number"
                                value={Number(row.offset) || 0}
                                onChange={(e) => updateRow(idx, { offset: Number(e.target.value) || 0 })}
                                helperText="- вліво, + вправо"
                                sx={{ width: { xs: "100%", md: 120 } }}
                            />
                            <TextField
                                label="Підказка"
                                value={row.clue || ""}
                                onChange={(e) => updateRow(idx, { clue: e.target.value })}
                                fullWidth
                                multiline
                                minRows={1}
                            />
                            <IconButton onClick={() => removeRow(idx)} aria-label="Видалити рядок">
                                <DeleteIcon />
                            </IconButton>
                        </Stack>
                    ))}

                    {rows.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                            Додайте рядки зі словами та підказками.
                        </Typography>
                    )}
                </Stack>
            </Box>

            <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Як налаштувати завдання "Сітка слів"</DialogTitle>
                <DialogContent>
                    <Stack spacing={1.5} sx={{ mt: 1 }}>
                        <Typography variant="body2">
                            1. У полі "Відповідь" введіть слово або фразу без пробілів між клітинками.
                        </Typography>
                        <Typography variant="body2">
                            2. Щоб літера була показана учню одразу, візьміть її у квадратні дужки: та[р]ас.
                        </Typography>
                        <Typography variant="body2">
                            3. Поле "Зсув" рухає рядок по клітинках: додатні числа вправо, від'ємні вліво.
                        </Typography>
                        <Typography variant="body2">
                            4. Підказки автоматично показуються під кросвордом у тому ж порядку, що й рядки.
                        </Typography>
                        <Typography variant="body2">
                            5. Колір відкритих літер застосовується до всіх літер, позначених квадратними дужками.
                        </Typography>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHelpOpen(false)}>Закрити</Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export default QuestionFormWordGrid;
